import {
  DEFAULT_MEASUREMENT,
  DEFAULT_MEASUREMENT_VALIDATION_RESULT,
  Measurement,
} from 'constants/MeasurementConstants';
import {DataFrame, DataFrameRow} from 'data-frames/DataFrameTypes';
import {PlateRecordHydratedDataQueryVariables} from 'samples/plate-maps/plate-record/__generated/PlateRecordHydratedDataQuery';
import {UnitsById} from 'samples/plate-maps/plate-record/PlateRecordUtil';
import {
  BioentitySource,
  INACCESSIBLE_GROUP_ID_SENTINEL,
  PlateMap,
  ReagentType,
  Source,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {getFlattenedWellIndex} from 'samples/plate-maps/PlateMapUtil';

export const CONTENT_LAYER_NAME = 'content';

export interface ContentConcentration {
  amount: number;
  unit: {apiId: string; versionNumber: number | null} | null;
}

export interface ContentPlacement {
  entityApiId: string;
  replicateIndex: number | null;
  fillGroupIndex: number | null;
  concentration: ContentConcentration | null;
  transferQuantity: ContentConcentration | null;
  testOrderIds: Array<string>;
}

export type ContentByWellPosition = Map<string, Array<ContentPlacement>>;

export interface ParsedContent {
  contentByWellPosition: ContentByWellPosition;
  // True when at least one source row carried a `_sample_name` without an `_entity_api_id`.
  hasUnregisteredSamples: boolean;
  // True when the backend has more rows than were fetched. The viewer should
  // surface this rather than render a partial plate.
  isTruncated: boolean;
}

export function wellPositionKey(rowIndex: number, columnIndex: number): string {
  return `${rowIndex},${columnIndex}`;
}

function asNullableString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  return typeof value === 'string' ? value : String(value);
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseContentAmount(
  rawAmount: unknown,
  rawUnitApiId: unknown,
  rawUnitVersionNumber: unknown
): ContentConcentration | null {
  const amount = asNullableNumber(rawAmount);
  if (amount == null) {
    return null;
  }
  const unitApiId = asNullableString(rawUnitApiId);
  return {
    amount,
    unit:
      unitApiId != null ? {apiId: unitApiId, versionNumber: asNullableNumber(rawUnitVersionNumber)} : null,
  };
}

function asStringArray(value: unknown): Array<string> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

/**
 * A row names either a single `_entity_api_id`, or (for a container-sourced row) a
 * `_container_contents_api_ids` list of every entity the container holds — there is no
 * per-entity concentration/transfer quantity in that case, so one placement is emitted per
 * contained entity, all sharing the row's single concentration/transfer quantity.
 */
function parsePlacements(row: DataFrameRow): Array<ContentPlacement> {
  const entityApiId = asNullableString(row._entity_api_id);
  const entityApiIds = entityApiId != null ? [entityApiId] : asStringArray(row._container_contents_api_ids);

  // Empty-well placeholders and named-only rows are skipped; named-only rows
  // are flagged separately on `ParsedContent.hasUnregisteredSamples`.
  if (entityApiIds.length === 0) {
    return [];
  }

  const replicateIndex = asNullableNumber(row._replicate_index);
  const fillGroupIndex = asNullableNumber(row._fill_group_index);
  const concentration = parseContentAmount(
    row._concentration_amount,
    row._concentration_unit_api_id,
    row._concentration_unit_version_number
  );
  const transferQuantity = parseContentAmount(
    row._transfer_quantity_amount,
    row._transfer_quantity_unit_api_id,
    row._transfer_quantity_unit_version_number
  );
  // A container-sourced row has no per-entity test orders, same as concentration/transfer quantity
  // above -- the row's test order(s), if any, are shared across every entity it holds.
  const testOrderIds = asStringArray(row._test_order_ids);

  return entityApiIds.map((id) => ({
    entityApiId: id,
    replicateIndex,
    fillGroupIndex,
    concentration,
    transferQuantity,
    testOrderIds,
  }));
}

interface BuildFilledPlateMapInput {
  // Base plate map from `ScratchPlateDesignSummaryUtil.buildPlateMap`; carries
  // everything except per-well `contents`.
  basePlateMap: PlateMap;
  contentByWellPosition: ContentByWellPosition;
  // Bioentity API id → its storable schema API id. Missing entries fall back
  // to `INACCESSIBLE_GROUP_ID_SENTINEL` so the "(No access)" chip renders.
  schemaIdsByReagentId?: {[id: string]: string | undefined};
  // Hydrated `MeasurementUnit` keyed by `(apiId, versionNumber)`. Missing
  // entries leave the well concentration rendered as a bare number.
  unitsById?: UnitsById;
  // Test order id → its display info (name, dilution factor), for the hover popover. Missing
  // entries render as an "invalid id" placeholder -- see getTestOrderInfosFromIds.
  testOrderInfoById?: TestOrderInfoById;
}

function buildMeasurement(
  concentration: ContentConcentration | null,
  unitsById: UnitsById
): Measurement | null {
  if (concentration == null) {
    return null;
  }
  const unitNode = concentration.unit;
  const hydratedUnit =
    unitNode != null ? (unitsById[unitNode.apiId]?.[unitNode.versionNumber ?? 1] ?? null) : null;
  return {value: concentration.amount, unit: hydratedUnit};
}

function buildContent(
  placement: ContentPlacement,
  schemaIdsByReagentId: {[id: string]: string | undefined},
  unitsById: UnitsById
): BioentitySource {
  const measurement = buildMeasurement(placement.concentration, unitsById);
  const tqMeasurement = buildMeasurement(placement.transferQuantity, unitsById);
  return {
    type: ReagentType.SOURCE_CONTAINABLE,
    id: placement.entityApiId,
    groupId: schemaIdsByReagentId[placement.entityApiId] ?? INACCESSIBLE_GROUP_ID_SENTINEL,
    sourceConcentration: {
      measurement: measurement ?? DEFAULT_MEASUREMENT,
      errors: DEFAULT_MEASUREMENT_VALIDATION_RESULT,
    },
    finalConcentration: {
      measurement: measurement ?? DEFAULT_MEASUREMENT,
      errors: DEFAULT_MEASUREMENT_VALIDATION_RESULT,
    },
    transferQuantity: {
      measurement: tqMeasurement ?? DEFAULT_MEASUREMENT,
      errors: DEFAULT_MEASUREMENT_VALIDATION_RESULT,
    },
    testOrders: placement.testOrderIds.length > 0 ? placement.testOrderIds.map((id) => ({id})) : undefined,
  };
}

/**
 * Fold per-well `contents` onto a base `PlateMap`. Other well fields
 * (role, schema tags, concentration layers) pass through untouched.
 */
export function buildFilledPlateMap(input: BuildFilledPlateMapInput): PlateMap {
  const {
    basePlateMap,
    contentByWellPosition,
    schemaIdsByReagentId = {},
    unitsById = {},
    testOrderInfoById = {},
  } = input;

  return {
    ...basePlateMap,
    testOrderDilutionInfoById: testOrderInfoById,
    wells: basePlateMap.wells.map((well) => {
      const wellKey = wellPositionKey(well.position.row, well.position.column);
      const placements = contentByWellPosition.get(wellKey) ?? [];
      if (placements.length === 0) {
        return well;
      }
      const sources = placements.map((placement) => buildContent(placement, schemaIdsByReagentId, unitsById));
      return {...well, sources, contents: []};
    }),
  };
}

/**
 * Group the rows of a filled scratch plate's `content` layer by well position;
 * placements appear in source-row order. `totalRowCount` is the data frame's
 * authoritative row count (e.g. `DataFrameNode.numRows`); when it exceeds the
 * number of rows in `contentDataFrame`, the result is flagged as truncated.
 */
export function parseContentDataFrame(contentDataFrame: DataFrame, totalRowCount: number): ParsedContent {
  const contentByWellPosition: ContentByWellPosition = new Map();
  let hasUnregisteredSamples = false;
  for (const row of contentDataFrame.rows) {
    const placements = parsePlacements(row);
    if (placements.length === 0) {
      if (asNullableString(row._entity_api_id) == null && asNullableString(row._sample_name) != null) {
        hasUnregisteredSamples = true;
      }
      continue;
    }
    const rowIndex = asNullableNumber(row._row_index);
    const columnIndex = asNullableNumber(row._column_index);
    if (rowIndex == null || columnIndex == null) {
      continue;
    }
    const key = wellPositionKey(rowIndex, columnIndex);
    const existing = contentByWellPosition.get(key);
    if (existing != null) {
      existing.push(...placements);
    } else {
      contentByWellPosition.set(key, [...placements]);
    }
  }
  return {
    contentByWellPosition,
    hasUnregisteredSamples,
    isTruncated: totalRowCount > contentDataFrame.rows.length,
  };
}

/**
 * Derive the variables needed to bulk-hydrate the bioentities and concentration
 * units referenced by a parsed content layer. Missing unit version numbers
 * fall back to `1`, matching the convention in the rest of the plate stack.
 */
export function buildHydrationVariablesFromContent(
  contentByWellPosition: ContentByWellPosition
): PlateRecordHydratedDataQueryVariables {
  const bioentityIds = new Set<string>();
  const testOrderIds = new Set<string>();
  const unitVariants = new Map<string, {apiId: string; versionNumber: number}>();
  const addUnit = (unit: ContentConcentration['unit']): void => {
    if (unit != null) {
      const versionNumber = unit.versionNumber ?? 1;
      unitVariants.set(`${unit.apiId}#${versionNumber}`, {apiId: unit.apiId, versionNumber});
    }
  };
  for (const placements of contentByWellPosition.values()) {
    for (const placement of placements) {
      bioentityIds.add(placement.entityApiId);
      addUnit(placement.concentration?.unit ?? null);
      addUnit(placement.transferQuantity?.unit ?? null);
      placement.testOrderIds.forEach((id) => testOrderIds.add(id));
    }
  }
  const variants = [...unitVariants.values()];
  return {
    bioentityIds: [...bioentityIds],
    storableIds: [],
    unitIds: variants.map((v) => v.apiId),
    unitVersionNumbers: variants.map((v) => v.versionNumber),
    testOrderIds: [...testOrderIds],
  };
}

export interface FilledScratchPlateData {
  plateName: string | null;
  plateMap: PlateMap;
  containerSchemaId: string | null;
  isTransferPlate: boolean;
  // True when the content data frame had more rows on the backend than were
  // fetched, so the plate map is built from a partial set of placements.
  isTruncated: boolean;
}

export function buildFilledPlateMapFromResolvedSources(
  basePlateMap: PlateMap,
  resolvedPlate: {[wellIndex: number]: Array<Source>},
  testOrderInfoById?: TestOrderInfoById
): PlateMap {
  return {
    ...basePlateMap,
    ...(testOrderInfoById != null ? {testOrderDilutionInfoById: testOrderInfoById} : {}),
    wells: basePlateMap.wells.map((well) => {
      const wellIndex = getFlattenedWellIndex(well.position.row, well.position.column, basePlateMap.size);
      const sources = resolvedPlate[wellIndex];
      if (sources == null || sources.length === 0) {
        return well;
      }
      return {
        ...well,
        sources,
        contents: [],
      };
    }),
  };
}
